<script setup>

// eslint-disable-next-line
const props = defineProps({
  questions: {
    type: Array,
    required: true
  }
});

// Helper to determine if the answer is considered a "mistake"
const isMistake = (result) => result && result.distance > 0;
</script>

<template>
  <div class="modal-overlay">
    <div class="modal-content">
      <header class="modal-header">
        <h2>Ergebnisse</h2>

        <RouterLink to="/">Zum Start</RouterLink>
      </header>

      <div class="results-list">
        <div v-for="(q, index) in props.questions" :key="index" class="result-card"
          :class="{ 'has-mistake': isMistake(q.result), 'is-skipped': !q.result }">
          <div class="status-indicator">
            <span v-if="q.result && q.result.distance < 1" class="dot green"></span>
            <span v-else-if="q.result && q.result.distance === 1" class="dot orange"></span>
            <span v-else-if="q.result && q.result.distance > 1" class="dot red"></span>
            <span v-else class="dot blue"></span>
          </div>

          <div class="word-info">
            <span class="target-word">{{ q.word }}</span>

            <div v-if="q.result" class="user-answer">
              <span class="label">Deine Antwort:</span>
              <span class="answer-text" :class="{ 'error-text': q.result.distance > 0 }">
                {{ q.result.text }}
              </span>
              <span class="distance-tag" v-if="q.result.distance > 0">
                (Distanz: {{ q.result.distance }})
              </span>
            </div>

            <div v-else class="user-answer skipped">
              Keine Antwort gegeben
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$bg-color: #f1f5f9;
$card-bg: #ffffff;
$error-bg: #fff1f2; // Light red tint for mistakes
$text-main: #1e293b;
$text-muted: #64748b;
$error-red: #e11d48;

.modal-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
  position: fixed;
  inset: 0;
}

.modal-content {
  width: 90%;
  max-width: 450px;
  max-height: 80vh;
  background: $bg-color;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  padding: 1.5rem 2rem;
  background: white;
  display: flex;
  justify-content: space-between;
  line-height: 2.5rem;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 800;
    color: $text-main;
  }

  a {
    background-color: #3b82f6;
    color:#fff;
    padding: 0.5rem 1.5rem;
    font-size: 1.5rem;
    border-radius: 1rem;
    text-decoration: none;
  }
}

.results-list {
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.result-card {
  background: $card-bg;
  padding: 1.25rem;
  border-radius: 18px;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &.has-mistake {
    background: $error-bg;
    border-color: rgba($error-red, 0.1);
  }

  &.is-skipped {
    opacity: 0.8;
  }
}

.dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: block;

  &.green {
    background: #22c55e;
    box-shadow: 0 0 10px #22c55e88;
  }

  &.orange {
    background: #f59e0b;
    box-shadow: 0 0 10px #f59e0b88;
  }

  &.red {
    background: #ef4444;
    box-shadow: 0 0 10px #ef444488;
  }

  &.blue {
    background: #3b82f6;
    box-shadow: 0 0 10px #3b82f688;
  }
}

.word-info {
  .target-word {
    display: block;
    font-weight: 700;
    font-size: 1.1rem;
    color: $text-main;
  }

  .user-answer {
    font-size: 0.9rem;
    color: $text-muted;
    margin-top: 4px;

    .error-text {
      color: $error-red;
      font-weight: 600;
      text-decoration: line-through;
    }

    .distance-tag {
      margin-left: 8px;
      font-size: 0.8rem;
      background: rgba(0, 0, 0, 0.05);
      padding: 2px 6px;
      border-radius: 6px;
    }
  }
}
</style>